<script setup>
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { getI18n } from '@/plugins/i18n'
import { DxDataGrid } from 'devextreme-vue/data-grid'
import { useGridRef, useODataSource, useDefaultEditing, useDefaultGridOptions, refreshGridAsync, getCreatedRowId } from '@/composables/data-grid'
import { useArchivedAtColumn, useArchivedColumn } from '@/composables/data-grid-column'
import { useArchive } from '@/composables/archive'
import { useWarehouseDeleteOptions } from '@/composables/delete-options'
import { useChips } from '@/composables/chips'

import { planningApi } from '@/apis/planning.api'
import { usePlanningStore } from '@/stores/planning.store'

import WarehouseMovementsGrid from './WarehouseMovementsGrid.vue'
import WarehouseEdit from '@/components/shared/WarehouseEdit.vue'
import ArchiveGridButton from '@/components/shared/ArchiveGridButton.vue'
import ArchivedGridToggle from '@/components/shared/ArchivedGridToggle.vue'
import ArchiveWarningDialog from '@/components/shared/ArchiveWarningDialog.vue'
import DeleteOptionsDialog from '@/components/shared/DeleteOptionsDialog.vue'
import ChipTemplate from '@/components/shared/ChipTemplate.vue'

const gridId = 'warehouses-grid-v2'
const i18n = getI18n()
const route = useRoute()
const planningStore = usePlanningStore()
const { getVirtualLocationChip } = useChips()

const { gridRef } = useGridRef()
const editItem = ref(null)

const { deleteOptions, onDeleteOptionSelect, onWarehouseBlocked } = useWarehouseDeleteOptions({
  onDeleted: () => updateGrid(),
})

const gridDataSource = useODataSource(planningApi.getAbsoluteUrl() + planningApi.warehousesUrl, {
  customParamsFn: () => showArchived.value ? { show_archived: true } : {},
})

const { archiveWarning, archiveButton, showArchived, archiveAsync, onRowPrepared: onArchiveRowPrepared, onRowRemoving } = useArchive({
  onArchive: async (id) => { await planningStore.archiveWarehouseAsync(id) },
  onDelete: async (id, config) => { await planningStore.deleteWarehouseAsync(id, config) },
  onDeleteBlocked: onWarehouseBlocked,
  onArchiveBlocked: onWarehouseBlocked,
  gridRef,
})

const columns = [
  {
    type: 'buttons',
    showInColumnChooser: false,
    buttons: [
      {
        name: 'edit',
        visible: (e) => !e.row?.data?.archived,
        onClick: (e) => { editItem.value = { ...e.row.data } },
      },
      { name: 'delete', visible: (e) => !e.row?.data?.archived },
      archiveButton,
    ],
    fixed: false,
    visibleIndex: -1,
    width: '7rem',
  },
  {
    dataField: 'name',
    caption: i18n.t('common.name'),
    validationRules: [{ type: 'required' }],
    sortOrder: 'asc',
    width: '12rem',
  },
  {
    dataField: 'is_virtual',
    caption: i18n.t('common.virtualLocation'),
    dataType: 'boolean',
    alignment: 'center',
    cellTemplate: 'virtual-location-cell',
    width: '9rem',
  },
  {
    dataField: 'description',
    caption: i18n.t('common.description'),
    width: '15rem',
  },
  {
    dataField: 'city',
    caption: i18n.t('common.city'),
    width: '10rem',
  },
  {
    dataField: 'zip_code',
    caption: i18n.t('common.zipCode'),
    width: '7rem',
  },
  {
    dataField: 'phone',
    caption: i18n.t('common.phone'),
    width: '10rem',
  },
  {
    dataField: 'email',
    caption: i18n.t('common.email'),
    width: '12rem',
  },
  useArchivedAtColumn(),
  useArchivedColumn(),
]

// When arriving from an item (e.g. equipment edit or universal search), filter the
// primary grid to the relevant warehouse and pass the service down to the detail
// movements grid.
const detailService = computed(() => {
  const id = Number(route.query.service)
  return id ? { id } : null
})
const initialWarehouseApplied = ref(false)

const expandRouteWarehouse = () => {
  const instance = gridRef.value?.instance
  const warehouseId = Number(route.query.warehouse)
  if (!instance || !warehouseId) return
  // Re-expand to remount the detail grid with the current service filter, so it also
  // applies when the query changes while already on this page.
  instance.collapseAll(-1)
  instance.expandRow(warehouseId)
}

const onContentReady = () => {
  if (initialWarehouseApplied.value || !route.query.warehouse) return
  initialWarehouseApplied.value = true
  expandRouteWarehouse()
}

watch(() => [route.query.warehouse, route.query.service], expandRouteWarehouse)

const gridOptions = useDefaultGridOptions({
  id: gridId,
  columns,
  dataSource: gridDataSource,
  editing: useDefaultEditing(),
  masterDetail: {
    enabled: true,
    template: 'master-detail',
  },
  onRowRemoving,
  onRowPrepared: onArchiveRowPrepared,
  onContentReady,
  clearFilterButton: true,
  archivedToggle: true,
  addButton: {
    onClick: () => { editItem.value = {} },
  },
})

const updateGrid = async (saved) => {
  const createdId = getCreatedRowId(editItem.value, saved)
  editItem.value = null
  await refreshGridAsync(gridRef, createdId)
}
</script>

<template>
  <div>
    <q-dialog :model-value="editItem !== null" class="dialog-md" @hide="updateGrid">
      <warehouse-edit
        v-if="editItem !== null"
        :model-value="editItem"
        @update="updateGrid"
        @cancel="updateGrid"
        @delete="updateGrid"
      />
    </q-dialog>
    <archive-warning-dialog
      v-model="archiveWarning"
      @confirm="async () => await archiveAsync(archiveWarning.item.id, false)"
    />
    <delete-options-dialog
      v-model="deleteOptions.show"
      :title="deleteOptions.title"
      :message="deleteOptions.message"
      :caption="deleteOptions.caption"
      :options="deleteOptions.options"
      @select="onDeleteOptionSelect"
    />
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
    >
      <template #master-detail="{ data: templateData }">
        <warehouse-movements-grid :warehouse="templateData.data" :service="detailService" />
      </template>
      <template #virtual-location-cell="{ data: cell }">
        <chip-template v-if="cell.value" :values="getVirtualLocationChip()" justify="center" />
      </template>
      <template #archive-button="{ data: row }">
        <archive-grid-button :row="row" />
      </template>
      <template #archived-toggle>
        <archived-grid-toggle
          :show-archived="showArchived"
          :grid-ref="gridRef"
          @update:show-archived="showArchived = $event"
        />
      </template>
    </dx-data-grid>
  </div>
</template>
